import type { ReactNode } from 'react'
import type { HeroCoordinates } from '@/components/heroes/hero-frame'
import type { MapPoint, MapView, RouteSample } from '@/lib/heroes/night-map'
import type { CSSVars } from '@/types/css'

import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClass } from '@/lib/ui'
import { onAdapted } from '@/lib/adaptation'
import { localSiderealHours } from '@/lib/astro/sky'
import { prefersReducedMotion } from '@/lib/preferences'
import { HeroFrame, useHero, useHeroProgress } from '@/components/heroes/hero-frame'
import styles from '@/components/heroes/visit-hero.module.css'

import {
    DEFAULT_VIEW,
    PLACES,
    RIVERS,
    ROADS,
    ROUTE_PATH,
    SKY_IN_TOWN,
    SKY_ON_THE_RIDGE,
    STARS_IN_TOWN,
    getContours,
    routeAt,
    starBands,
    starsVisible,
    viewFor
} from '@/lib/heroes/night-map'

import { OBSERVATORY, OPENING_HOURS } from '@/constants/observatory'

const TITLE_ID = 'visit-hero-title'
const MASK_ID = 'visit-hero-route-mask'

/** Scroll progress over which the drive is made, from the M5 to the observatory gate. */
const ROUTE_START = 0.05
const ROUTE_END = 0.74
/** Then the dome opens, the beam rises and the details arrive. */
const SLIT_START = 0.75
const SLIT_END = 0.83
const BEAM_START = 0.79
const BEAM_END = 0.92
const DETAILS_START = 0.85
const DETAILS_END = 0.96

/** Glow radius of each town in map units. */
const TOWN_GLOWS: { key: string, at: MapPoint, radius: number }[] = [
    { key: 'taunton', at: PLACES.taunton, radius: 560 },
    { key: 'wellington', at: PLACES.wellington, radius: 190 },
    { key: 'tiverton', at: PLACES.tiverton, radius: 230 }
]

type PlaceLabel = {
    text: string
    at: MapPoint
    dx: number
    dy: number
    anchor: 'start' | 'middle' | 'end'
    /** Close to the observatory: left out when the map is zoomed out so far it would crowd the dome. */
    near?: boolean
}

const PLACE_LABELS: PlaceLabel[] = [
    { text: 'TAUNTON', at: PLACES.taunton, dx: -10, dy: -9, anchor: 'end' },
    { text: 'WELLINGTON', at: PLACES.wellington, dx: 0, dy: 16, anchor: 'middle' },
    { text: 'TIVERTON', at: PLACES.tiverton, dx: -10, dy: 4, anchor: 'end' },
    { text: 'DULVERTON', at: PLACES.dulverton, dx: -10, dy: 3, anchor: 'end' },
    { text: 'WHEDDON CROSS', at: PLACES.wheddonCross, dx: 10, dy: 12, anchor: 'start', near: true },
    { text: 'EXFORD', at: PLACES.exford, dx: -8, dy: 14, anchor: 'end' },
    { text: 'MINEHEAD', at: PLACES.minehead, dx: 8, dy: 3, anchor: 'start' },
    { text: 'PORLOCK', at: PLACES.porlock, dx: -8, dy: 3, anchor: 'end' }
]

const clamp01 = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value)

const smoothstep = (edge0: number, edge1: number, value: number): number => {
    const t = clamp01((value - edge0) / (edge1 - edge0))

    return t * t * (3 - 2 * t)
}

const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2

const viewBoxOf = (view: MapView): string => `${view.x.toFixed(2)} ${view.y.toFixed(2)} ${view.w.toFixed(2)} ${view.h.toFixed(2)}`

const percent = (value: number): string => `${value.toFixed(3)}%`

const formatCount = (count: number): string => count.toLocaleString('en-GB')

/** The contour lines, built once for the server and the browser alike. */
const ContourLines = memo(function ContourLines(): ReactNode {
    return (
        <g>
            {getContours().map(contour => {
                const style: CSSVars = { '--d': `${contour.delay}s`, '--t': `${contour.duration}s` }

                return (
                    <path
                        key={contour.key}
                        d={contour.d}
                        pathLength={1}
                        className={cn(styles.trace, contour.kind === 'coast' ? styles.coast : contour.kind === 'index' ? styles.index : styles.line)}
                        style={style}
                    />
                )
            })}
        </g>
    )
})

/** Rivers and roads, faint beneath the contours. */
const MapLines = memo(function MapLines(): ReactNode {
    return (
        <g>
            {RIVERS.map(river => {
                const style: CSSVars = { '--d': `${river.delay}s`, '--t': `${river.duration}s` }

                return <path key={river.key} d={river.d} pathLength={1} className={cn(styles.trace, styles.river)} style={style} />
            })}
            {ROADS.map(road => {
                const style: CSSVars = { '--d': `${road.delay}s`, '--t': `${road.duration}s` }

                return <path key={road.key} d={road.d} pathLength={1} className={cn(styles.trace, road.major ? styles.motorway : styles.road)} style={style} />
            })}
        </g>
    )
})

interface PlaceLabelsProps {
    /** Map units in one screen pixel, so the lettering keeps its size on every screen. */
    unit: number
}

const PlaceLabels = ({ unit }: PlaceLabelsProps): ReactNode => (
    <g className={styles.labels}>
        <text x={-430} y={130} fontSize={16 * unit} className={styles.region}>
            Exmoor
        </text>
        <text x={-300} y={-300} fontSize={13 * unit} className={styles.region}>
            Bristol Channel
        </text>
        <text x={560} y={120} fontSize={13 * unit} className={styles.region} textAnchor='middle'>
            Vale of Taunton
        </text>

        <g transform={`translate(${PLACES.dunkery.x} ${PLACES.dunkery.y}) scale(${unit})`}>
            <path d='M 0 -5 L 4.5 3 L -4.5 3 Z' className={styles.peak} />
            <text x={-9} y={3} fontSize={9} textAnchor='end' className={styles.place}>
                DUNKERY BEACON
            </text>
        </g>

        {PLACE_LABELS.filter(label => !label.near || unit < 1.6).map(label => (
            <g key={label.text} transform={`translate(${label.at.x} ${label.at.y}) scale(${unit})`}>
                <circle r={2} className={styles.town} />
                <text x={label.dx} y={label.dy} fontSize={9} textAnchor={label.anchor} className={styles.place}>
                    {label.text}
                </text>
            </g>
        ))}
    </g>
)

type Stage = {
    width: number
    height: number
    view: MapView
    measured: boolean
}

const VisitScene = (): ReactNode => {
    const { reduced, pinned, skipped } = useHero()
    const stageRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<SVGSVGElement | null>(null)
    const washRef = useRef<HTMLDivElement | null>(null)
    const shadeRef = useRef<HTMLDivElement | null>(null)
    const glowRefs = useRef<(HTMLDivElement | null)[]>([])
    const bandRefs = useRef<(SVGGElement | null)[]>([])
    const maskRef = useRef<SVGPathElement | null>(null)
    const markerRef = useRef<SVGGElement | null>(null)
    const markerTextRef = useRef<SVGTextElement | null>(null)
    const leftShellRef = useRef<SVGPathElement | null>(null)
    const rightShellRef = useRef<SVGPathElement | null>(null)
    const slitRef = useRef<SVGPathElement | null>(null)
    const beamRef = useRef<HTMLDivElement | null>(null)
    const counterRef = useRef<HTMLSpanElement | null>(null)
    const skyRef = useRef<HTMLSpanElement | null>(null)
    const cueRef = useRef<HTMLDivElement | null>(null)
    const detailsRef = useRef<HTMLDivElement | null>(null)
    const progressRef = useRef(0)
    const forcedRef = useRef(false)
    const applyRef = useRef<(progress: number) => void>(() => undefined)
    const [stage, setStage] = useState<Stage>({ width: 1440, height: 810, view: DEFAULT_VIEW, measured: false })
    const [traced, setTraced] = useState(false)
    const [instant, setInstant] = useState(false)
    const { view } = stage
    const unit = view.w / stage.width
    const viewBox = viewBoxOf(view)
    const stars = useMemo(() => (stage.measured ? starBands(stage.view, stage.width, stage.height) : null), [stage])
    const at = (point: MapPoint) => ({ left: percent(((point.x - view.x) / view.w) * 100), top: percent(((point.y - view.y) / view.h) * 100) })
    const site = at(PLACES.observatory)
    const town = at(PLACES.taunton)
    const washStyle: CSSVars = { '--hx': town.left, '--hy': town.top }
    // Hairlines are drawn in map units scaled to the screen, so dashes and widths stay true on every device.
    const mapStyle: CSSVars = { '--u': unit.toFixed(4) }

    useHeroProgress(progress => {
        progressRef.current = progress

        // After "Skip intro" the scene holds the finished map until the visitor scrolls back into it.
        if (progress < 0.9) forcedRef.current = false

        applyRef.current(progress)
    })

    // Frame the map for this screen.
    useEffect(() => {
        const element = stageRef.current

        if (!element) return

        const measure = () => {
            const width = element.clientWidth
            const height = element.clientHeight

            if (!width || !height) return

            setStage(current => (current.measured && current.width === width && current.height === height
                ? current
                : { width, height, view: viewFor(width, height), measured: true }))
        }

        const observer = new ResizeObserver(measure)

        observer.observe(element)
        measure()

        return () => observer.disconnect()
    }, [])

    // The contours trace themselves once the dark-adaptation intro lifts.
    useEffect(() => {
        let first = 0
        let second = 0
        let begun = false

        const start = () => {
            if (begun) return

            begun = true
            // Two frames, so the undrawn state is on screen before the transition begins.
            first = requestAnimationFrame(() => {
                second = requestAnimationFrame(() => setTraced(true))
            })
        }

        const stopAdapted = onAdapted(start)
        const fallback = window.setTimeout(start, 6500)

        return () => {
            stopAdapted()
            window.clearTimeout(fallback)
            cancelAnimationFrame(first)
            cancelAnimationFrame(second)
        }
    }, [])

    useEffect(() => {
        if (!reduced) return

        setInstant(true)
        setTraced(true)
    }, [reduced])

    useEffect(() => {
        if (!skipped) return

        forcedRef.current = true
        setInstant(true)
        setTraced(true)
        applyRef.current(1)
    }, [skipped])

    // Everything that follows the scroll is written straight to the DOM.
    useEffect(() => {
        const still = reduced || prefersReducedMotion()
        const sample: RouteSample = { x: PLACES.junction27.x, y: PLACES.junction27.y, minutes: 0, road: 'A361' }
        const centreX = view.x + view.w / 2
        // On phones the counter sits top right, level with the dome: the label stays left of the marker there.
        const narrow = stage.width < 640
        const last ={ minute: -1, road: '', count: -1, sky: '', side: '', details: -1 }

        const setOpacity = (element: HTMLElement | SVGElement | null, value: number) => {
            if (element) element.style.opacity = String(Math.round(value * 1000) / 1000)
        }

        const apply = (raw: number) => {
            const progress = still || forcedRef.current ? 1 : raw
            const drive = easeInOutSine(clamp01((progress - ROUTE_START) / (ROUTE_END - ROUTE_START)))
            const dark = drive

            routeAt(drive, sample)

            // The dotted route draws itself behind a mask, with the car's marker and the minutes riding along.
            maskRef.current?.setAttribute('stroke-dashoffset', String(Math.round((1 - drive) * 10000) / 10000))

            const marker = markerRef.current

            if (marker) {
                marker.setAttribute('transform', `translate(${sample.x.toFixed(1)} ${sample.y.toFixed(1)})`)
                marker.setAttribute('opacity', String(Math.round(smoothstep(0, 0.015, drive) * (1 - smoothstep(0.965, 1, drive)) * 1000) / 1000))
            }

            const minute = Math.round(sample.minutes)
            const text = markerTextRef.current

            if (text && (minute !== last.minute || sample.road !== last.road)) {
                last.minute = minute
                last.road = sample.road
                text.textContent = `${minute} min · ${sample.road}`
            }

            // Keep the label on the side of the marker with room for it.
            const side = narrow || sample.x > centreX + view.w * 0.2 ? 'left' : 'right'

            if (text && side !== last.side) {
                last.side = side
                text.setAttribute('x', side === 'left' ? '-13' : '13')
                text.setAttribute('text-anchor', side === 'left' ? 'end' : 'start')
            }

            // The town glow retreats and the night deepens.
            glowRefs.current.forEach((glow, index) => {
                if (!glow) return

                const shrink = index === 0 ? 0.55 : 0.7

                glow.style.transform = `translate(-50%, -50%) scale(${(1 - shrink * dark).toFixed(4)})`
                setOpacity(glow, 1 - (index === 0 ? 0.74 : 0.9) * dark)
            })

            setOpacity(washRef.current, 1 - dark)
            setOpacity(shadeRef.current, 0.2 + 0.68 * dark)
            setOpacity(mapRef.current, 1 - 0.3 * dark)

            bandRefs.current.forEach((band, index) => {
                if (!band) return

                const value = index === 0
                    ? 0.55 + 0.45 * smoothstep(0, 0.3, dark)
                    : index === 1
                        ? smoothstep(0.1, 0.45, dark)
                        : index === 2
                            ? smoothstep(0.32, 0.72, dark)
                            : smoothstep(0.58, 0.96, dark)

                setOpacity(band, value)
            })

            const count = starsVisible(dark)

            if (count !== last.count && counterRef.current) {
                last.count = count
                counterRef.current.textContent = formatCount(count)
            }

            const sky = (SKY_IN_TOWN + (SKY_ON_THE_RIDGE - SKY_IN_TOWN) * dark).toFixed(1)

            if (sky !== last.sky && skyRef.current) {
                last.sky = sky
                skyRef.current.textContent = sky
            }

            // At the gate the dome opens its slit and starlight rises.
            const open = smoothstep(SLIT_START, SLIT_END, progress)

            leftShellRef.current?.setAttribute('transform', `translate(${(-3.2 * open).toFixed(2)} 0)`)
            rightShellRef.current?.setAttribute('transform', `translate(${(3.2 * open).toFixed(2)} 0)`)
            slitRef.current?.setAttribute('opacity', String(Math.round(open * 1000) / 1000))

            const beam = smoothstep(BEAM_START, BEAM_END, progress)

            if (beamRef.current) {
                beamRef.current.style.transform = `scaleY(${beam.toFixed(4)})`
                setOpacity(beamRef.current, beam)
            }

            const details = Math.round(smoothstep(DETAILS_START, DETAILS_END, progress) * 500) / 500

            if (details !== last.details && detailsRef.current) {
                last.details = details
                detailsRef.current.style.opacity = String(details)
                detailsRef.current.style.visibility = details > 0.01 ? 'visible' : 'hidden'
                detailsRef.current.style.transform = details < 1 ? `translate3d(0, ${((1 - details) * 12).toFixed(1)}px, 0)` : 'none'
            }

            setOpacity(cueRef.current, still ? 0 : 1 - smoothstep(0.01, 0.05, progress))
        }

        applyRef.current = apply
        apply(progressRef.current)

        return () => {
            applyRef.current = () => undefined
        }
    }, [reduced, view, stars, stage.width])

    return (
        <>
            <div ref={stageRef} aria-hidden className={cn('absolute inset-0 overflow-hidden', styles.stage)}>
                <div ref={washRef} className={styles.wash} style={washStyle} />

                <svg
                    ref={mapRef}
                    viewBox={viewBox}
                    preserveAspectRatio='xMidYMid slice'
                    className={cn('absolute inset-0 h-full w-full', styles.layer, traced && styles.traced, instant && styles.instant)}
                    style={mapStyle}
                >
                    <MapLines />
                    <ContourLines />
                    <PlaceLabels unit={unit} />
                </svg>

                {stars && (
                    <svg viewBox={viewBox} preserveAspectRatio='xMidYMid slice' className='absolute inset-0 h-full w-full'>
                        {stars.map((band, index) => (
                            <g
                                key={index}
                                ref={element => {
                                    bandRefs.current[index] = element
                                }}
                                style={{ opacity: 0 }}
                                className={styles.stars}
                            >
                                {band.map((star, starIndex) => (
                                    <circle key={starIndex} cx={star.x.toFixed(1)} cy={star.y.toFixed(1)} r={(star.r * unit).toFixed(2)} opacity={star.opacity.toFixed(2)} />
                                ))}
                            </g>
                        ))}
                    </svg>
                )}

                {TOWN_GLOWS.map((glow, index) => {
                    const position = at(glow.at)
                    const size = `${((glow.radius * 2) / unit).toFixed(1)}px`

                    return (
                        <div
                            key={glow.key}
                            ref={element => {
                                glowRefs.current[index] = element
                            }}
                            className={styles.halo}
                            style={{ left: position.left, top: position.top, width: size, height: size }}
                        />
                    )
                })}

                <div ref={shadeRef} className={styles.shade} />

                <svg viewBox={viewBox} preserveAspectRatio='xMidYMid slice' className='absolute inset-0 h-full w-full'>
                    <defs>
                        <mask id={MASK_ID} maskUnits='userSpaceOnUse' x={view.x} y={view.y} width={view.w} height={view.h}>
                            <path
                                ref={maskRef}
                                d={ROUTE_PATH}
                                pathLength={1}
                                fill='none'
                                stroke='white'
                                strokeWidth={26 * unit}
                                strokeLinecap='butt'
                                strokeLinejoin='round'
                                strokeDasharray='1 2'
                                strokeDashoffset={1}
                            />
                        </mask>
                    </defs>

                    <path d={ROUTE_PATH} vectorEffect='non-scaling-stroke' className={styles.ghost} />
                    <path
                        d={ROUTE_PATH}
                        mask={`url(#${MASK_ID})`}
                        strokeWidth={2.4 * unit}
                        strokeDasharray={`0 ${(7 * unit).toFixed(2)}`}
                        className={styles.route}
                    />

                    <g transform={`translate(${PLACES.junction27.x} ${PLACES.junction27.y}) scale(${unit})`}>
                        <circle r={4} className={styles.startRing} />
                        <text y={19} textAnchor='middle' className={styles.markerText}>
                            M5 · J27
                        </text>
                    </g>

                    <g ref={markerRef} transform={`translate(${PLACES.junction27.x} ${PLACES.junction27.y})`} opacity={0}>
                        <g transform={`scale(${unit})`}>
                            <circle r={9} className={styles.markerRing} />
                            <circle r={3.2} className={styles.markerDot} />
                            <text ref={markerTextRef} x={13} y={-10} className={styles.markerText}>
                                0 min · A361
                            </text>
                        </g>
                    </g>
                </svg>

                <div className={styles.site} style={{ left: site.left, top: site.top }}>
                    <span className={styles.pulse} />
                    <span className={cn(styles.pulse, styles.pulseLate)} />
                    <div ref={beamRef} className={styles.beam} />
                    <svg viewBox='-22 -22 44 36' className={styles.dome}>
                        <path d='M -18 6.5 H 18' className={styles.domeBase} />
                        <rect x={-12} y={0} width={24} height={6} className={styles.domeDrum} />
                        <path ref={slitRef} d='M 0 -9.5 V -0.5' className={styles.domeSlit} opacity={0} />
                        <path ref={leftShellRef} d='M -11 0 A 11 11 0 0 1 0 -11 V 0 Z' className={styles.domeShell} transform='translate(0 0)' />
                        <path ref={rightShellRef} d='M 0 -11 A 11 11 0 0 1 11 0 H 0 Z' className={styles.domeShell} transform='translate(0 0)' />
                    </svg>
                    <p className={cn('label-mono text-[0.6rem] text-brass max-sm:hidden', styles.domeLabel)}>
                        CosmicLens · 420 m
                    </p>
                </div>
            </div>

            <p className='sr-only'>
                A night map of the drive to the observatory, drawn in contour lines: from the M5 at junction 27 along the A361, north on the A396 through Dulverton to Wheddon Cross and up Hollowmoor Lane, about fifty minutes. As the glow of Taunton falls behind, the stars visible overhead rise from about 300 to about 4,500, and the dome opens its slit at the end.
            </p>

            <span aria-hidden className={styles.legibility} />

            <div className='pointer-events-none absolute right-5 top-[7.25rem] z-10 text-right sm:right-8 sm:top-[5.25rem] lg:right-12'>
                <div className={styles.rise}>
                    <p className='label-mono text-star-3'>
                        Stars visible overhead
                    </p>
                    <p className='mt-2 font-mono text-[2rem] leading-none tabular-nums tracking-[0.02em] text-star sm:text-5xl'>
                        <span ref={counterRef}>
                            {formatCount(STARS_IN_TOWN)}
                        </span>
                    </p>
                    <p className='label-mono mt-2.5 text-brass'>
                        <span className='sr-only'>
                            {'Sky brightness '}
                        </span>
                        <span ref={skyRef}>
                            {SKY_IN_TOWN.toFixed(1)}
                        </span>
                        {' mag/arcsec²'}
                    </p>
                </div>
            </div>

            <div className='pointer-events-none absolute inset-x-0 bottom-0 z-10 pb-[4.5rem] sm:pb-16'>
                <div className='container-page'>
                    <div className='max-w-[40rem]'>
                        <p className={cn('label-mono text-brass [@media(max-height:560px)]:hidden', styles.rise)}>
                            Hollowmoor Ridge · Exmoor Dark Sky Reserve
                        </p>
                        <h1
                            id={TITLE_ID}
                            className={cn('display-serif mt-3 text-[2.75rem] text-star sm:text-6xl lg:text-7xl [@media(max-height:560px)]:text-4xl', styles.rise, styles.second)}
                        >
                            Finding the dark
                        </h1>
                        <div className='relative mt-5 sm:mt-6'>
                            {pinned && (
                                <div ref={cueRef} className='absolute left-0 top-1'>
                                    <p className={cn('label-mono flex items-center gap-3 text-star-3', styles.rise, styles.third)}>
                                        <span aria-hidden className={cn('block h-px w-8 bg-brass', styles.cue)} />
                                        Scroll to drive up from the M5
                                    </p>
                                </div>
                            )}
                            <div ref={detailsRef} className={cn('grid gap-x-10 gap-y-4 sm:grid-cols-[auto_1fr]', styles.late)}>
                                <address className='not-italic'>
                                    <p className='label-mono text-brass'>
                                        {OBSERVATORY.name}
                                    </p>
                                    <p className='mt-1.5 text-[0.95rem] leading-snug text-star sm:text-base'>
                                        {OBSERVATORY.addressLines.map((line, index) => (
                                            <span key={line} className='sm:block'>
                                                {line}
                                                {index < OBSERVATORY.addressLines.length - 1 && (
                                                    <span className='sm:hidden'>
                                                        {', '}
                                                    </span>
                                                )}
                                            </span>
                                        ))}
                                    </p>
                                </address>
                                <p className='data-mono text-[0.75rem] leading-snug text-star-2 sm:hidden [@media(max-height:560px)]:block'>
                                    {OPENING_HOURS.map(row => `${row.days} ${row.hours}`).join(' · ')}
                                </p>
                                <div className='max-sm:hidden [@media(max-height:560px)]:hidden'>
                                    <p className='label-mono text-star-3'>
                                        Opening hours
                                    </p>
                                    <dl className='mt-1.5 grid gap-y-0.5 text-sm'>
                                        {OPENING_HOURS.map(row => (
                                            <div key={row.days} className='flex flex-wrap gap-x-3'>
                                                <dt className='text-star-2'>
                                                    {row.days}
                                                </dt>
                                                <dd className='data-mono text-star'>
                                                    {row.hours}
                                                </dd>
                                            </div>
                                        ))}
                                    </dl>
                                </div>
                                <a
                                    href={OBSERVATORY.directionsUrl}
                                    target='_blank'
                                    rel='noreferrer'
                                    className={buttonClass('primary', 'pointer-events-auto justify-self-start sm:col-span-2')}
                                >
                                    Get directions
                                    <ArrowUpRight aria-hidden className='size-4' strokeWidth={1.25} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

interface VisitHeroProps {
    /** The id of the first section after the hero; "Skip intro" lands there. */
    contentId: string
}

/**
 * Visit hero: a contour map of the drive from the M5 up to Hollowmoor Ridge. As the visitor scrolls, the route
 * draws itself, Taunton's glow falls behind and the stars come out, until the dome opens on a beam of starlight
 * and the address, hours and directions arrive.
 */
export const VisitHero = ({ contentId }: VisitHeroProps): ReactNode => {
    const [zenith, setZenith] = useState<HeroCoordinates | null>(null)

    // Straight overhead at the observatory, right now: its right ascension is the local sidereal time.
    useEffect(() => {
        try {
            setZenith({ ra: localSiderealHours(new Date()), dec: OBSERVATORY.latitude })
        }
        catch {
            // The corner label keeps its placeholder.
        }
    }, [])

    return (
        <HeroFrame
            id='visit-hero'
            label='Visit'
            coordinates={zenith}
            screens={2}
            contentId={contentId}
            titleId={TITLE_ID}
        >
            <VisitScene />
        </HeroFrame>
    )
}
